import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import { ISSUE_TYPES } from '../questions/ChartAnnotation';
import { api } from '../../lib/api';

const labelForIssue = (slug) => ISSUE_TYPES.find((t) => t.value === slug)?.label || slug;
const labelForDifficulty = (key) => ({
  starter: { label: 'Starter', tone: 'success' },
  standard: { label: 'Standard', tone: 'primary' },
  intermediate: { label: 'Intermediate', tone: 'warning' },
  advanced: { label: 'Advanced', tone: 'danger' },
})[key] || { label: 'Standard', tone: 'primary' };

function formatTime(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

function PatientSummary({ patient = {}, allergies = [], context }) {
  const lines = [];
  if (patient.age) lines.push(`Age ${patient.age}`);
  if (patient.gender) lines.push(patient.gender);
  if (patient.weight) lines.push(`Weight ${patient.weight}`);
  if (patient.creatinine) lines.push(`Creatinine ${patient.creatinine}`);
  if (patient.crcl) lines.push(`CrCl ${patient.crcl}`);
  return (
    <div className="spot-error__patient">
      <h5 className="mb-1">{patient.name || 'Patient'}</h5>
      <div className="text-muted small">{lines.join(' · ')}</div>
      {allergies.length ? (
        <div className="mt-2">
          <strong>Allergies:</strong> {allergies.map((a) => `${a.drug}${a.reaction ? ` (${a.reaction})` : ''}`).join('; ')}
        </div>
      ) : null}
      {context ? <div className="mt-2 small">{context}</div> : null}
    </div>
  );
}

function ChartFlagger({ prescriptionList, flags, onFlag, onClear, disabled }) {
  return (
    <div className="spot-error__chart">
      {prescriptionList.map((rx) => {
        const flagged = flags[rx.drug];
        return (
          <div key={rx.drug} className="spot-error__row">
            <div className="spot-error__rx">
              <strong>{rx.drug}</strong>
              <div className="text-muted small">
                {rx.dose ? `${rx.dose} · ` : ''}{rx.frequency || ''}{rx.route ? ` · ${rx.route}` : ''}
              </div>
              {rx.indication ? <div className="small text-muted">Indication: {rx.indication}</div> : null}
            </div>
            <Form.Select
              size="sm"
              value={flagged || ''}
              disabled={disabled}
              onChange={(e) => {
                if (!e.target.value) onClear(rx.drug);
                else onFlag(rx.drug, e.target.value);
              }}
            >
              <option value="">No issue</option>
              {ISSUE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </Form.Select>
          </div>
        );
      })}
    </div>
  );
}

function ResultPanel({ session, drill, onPlayAgain, onBackToList, backToListLabel }) {
  if (!session) return null;
  const grading = session.grading || {};
  const expected = grading.expectedIssues || [];
  const overall = session.recallPct === 100 && session.precisionPct === 100;
  return (
    <div className="spot-error__result">
      <div className="spot-error__scoreboard">
        <div className="spot-error__score-card">
          <div className="spot-error__score-card__label">Caught</div>
          <div className="spot-error__score-card__value">{grading.truePositives}/{expected.length}</div>
        </div>
        <div className="spot-error__score-card">
          <div className="spot-error__score-card__label">Recall</div>
          <div className="spot-error__score-card__value">{Math.round(session.recallPct)}%</div>
        </div>
        <div className="spot-error__score-card">
          <div className="spot-error__score-card__label">Precision</div>
          <div className="spot-error__score-card__value">{Math.round(session.precisionPct)}%</div>
        </div>
        <div className="spot-error__score-card">
          <div className="spot-error__score-card__label">F1</div>
          <div className="spot-error__score-card__value">{Math.round(session.f1Pct)}%</div>
        </div>
      </div>
      {overall ? (
        <Alert variant="success">All errors caught with no false positives.</Alert>
      ) : null}
      {grading.missed?.length ? (
        <div className="mt-3">
          <h5>Missed errors</h5>
          {grading.missed.map((m, idx) => (
            <Card key={idx} className="mb-2 border-danger">
              <Card.Body>
                <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                  <strong>{m.drug}</strong>
                  <Badge bg="danger">{labelForIssue(m.issueType)}</Badge>
                </div>
                {m.explanation ? <div>{m.explanation}</div> : null}
              </Card.Body>
            </Card>
          ))}
        </div>
      ) : null}
      {grading.wronglyFlagged?.length ? (
        <div className="mt-3">
          <h5>Wrongly flagged or wrong category</h5>
          {grading.wronglyFlagged.map((w, idx) => {
            const expectedForDrug = expected.find((e) => String(e.drug || '').toLowerCase() === String(w.drug || '').toLowerCase());
            return (
              <Card key={idx} className="mb-2 border-warning">
                <Card.Body>
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                    <strong>{w.drug}</strong>
                    <Badge bg="warning" text="dark">You said: {labelForIssue(w.issueType)}</Badge>
                    {expectedForDrug ? <Badge bg="info">Expected: {labelForIssue(expectedForDrug.issueType)}</Badge> : <Badge bg="secondary">No issue expected</Badge>}
                  </div>
                  {expectedForDrug?.explanation ? <div>{expectedForDrug.explanation}</div> : null}
                </Card.Body>
              </Card>
            );
          })}
        </div>
      ) : null}
      <div className="mt-4 d-flex gap-2 flex-wrap">
        <Button variant="primary" onClick={onPlayAgain}>Play again</Button>
        <Button variant="outline-secondary" onClick={onBackToList}>{backToListLabel || 'Back to exercises'}</Button>
      </div>
    </div>
  );
}

function ActiveDrill({ session, drill, onSubmit, onAbandon }) {
  const startedAt = useMemo(() => Date.now(), []);
  const limit = session.snapshot?.timeLimitSeconds || drill?.timeLimitSeconds || 90;
  const [now, setNow] = useState(Date.now());
  const [flags, setFlags] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Tick every second for the countdown.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const elapsed = Math.floor((now - startedAt) / 1000);
  const remaining = Math.max(0, limit - elapsed);
  const expired = remaining === 0;

  const flagsList = useMemo(() => Object.entries(flags)
    .filter(([, t]) => t)
    .map(([drugName, issueType]) => ({ drug: drugName, issueType })), [flags]);

  const submit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(flagsList, elapsed);
    } finally {
      setSubmitting(false);
    }
  }, [flagsList, elapsed, onSubmit, submitting]);

  // Auto-submit on time-out.
  useEffect(() => {
    if (expired && !submitting) submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expired]);

  const scenario = session.snapshot?.scenario || {};
  const prescriptions = Array.isArray(scenario.prescriptionList) ? scenario.prescriptionList : [];

  return (
    <div className="spot-error__active">
      <div className="spot-error__timer-bar">
        <div className={`spot-error__timer ${remaining < 15 ? 'spot-error__timer--low' : ''}`}>
          ⏱ {formatTime(remaining)}
        </div>
        <div className="text-muted small">
          {flagsList.length} flag{flagsList.length === 1 ? '' : 's'} placed · {prescriptions.length} prescriptions
        </div>
      </div>

      <PatientSummary patient={scenario.patient} allergies={scenario.allergies} context={scenario.context} />

      <ChartFlagger
        prescriptionList={prescriptions}
        flags={flags}
        onFlag={(d, t) => setFlags((s) => ({ ...s, [d]: t }))}
        onClear={(d) => setFlags((s) => { const n = { ...s }; delete n[d]; return n; })}
        disabled={submitting || expired}
      />

      <div className="d-flex gap-2 flex-wrap mt-3">
        <Button variant="primary" onClick={submit} disabled={submitting || expired}>
          {submitting ? <><Spinner animation="border" size="sm" /> Submitting…</> : 'Submit flags'}
        </Button>
        <Button variant="outline-secondary" onClick={onAbandon} disabled={submitting}>Abandon</Button>
      </div>
    </div>
  );
}

// Client-side mirror of server gradeSpotErrorSubmission. Used for the
// educator preview where the unsaved exercise can't be submitted to the
// real /submit endpoint. Must stay in sync with the server function
// (server/index.js).
function gradeLocallyForPreview(expectedIssues, submittedFlags) {
  const expected = Array.isArray(expectedIssues) ? expectedIssues : [];
  const submitted = Array.isArray(submittedFlags) ? submittedFlags : [];
  const key = (iss) => `${String(iss.drug || '').toLowerCase().trim()}::${iss.issueType}`;
  const expectedSet = new Set(expected.map(key));
  const submittedSet = new Set(submitted.map(key));
  let truePositives = 0;
  submittedSet.forEach((k) => { if (expectedSet.has(k)) truePositives += 1; });
  const falsePositives = submittedSet.size - truePositives;
  const falseNegatives = expectedSet.size - truePositives;
  const missed = expected.filter((iss) => !submittedSet.has(key(iss)));
  const wronglyFlagged = submitted.filter((sub) => !expectedSet.has(key(sub)));
  const precision = submittedSet.size === 0 ? 0 : truePositives / submittedSet.size;
  const recall = expectedSet.size === 0 ? 1 : truePositives / expectedSet.size;
  const f1 = (precision + recall) === 0 ? 0 : (2 * precision * recall) / (precision + recall);
  return {
    truePositives,
    falsePositives,
    falseNegatives,
    missed,
    wronglyFlagged,
    expectedIssues: expected,
    precisionPct: Number((precision * 100).toFixed(2)),
    recallPct: Number((recall * 100).toFixed(2)),
    f1Pct: Number((f1 * 100).toFixed(2)),
  };
}

const StudentSpotErrorWorkspace = ({
  token,
  onBack,
  // Preview mode: render an unsaved exercise without hitting the server.
  // Used by the spot-the-error editor's Preview button.
  previewMode = false,
  previewPayload = null,
  previewTitle = '',
  onExitPreview,
}) => {
  const [drills, setDrills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSession, setActiveSession] = useState(null);
  const [activeDrill, setActiveDrill] = useState(null);
  const [resultSession, setResultSession] = useState(null);
  const startingRef = useRef(null);

  // Preview flow: short-circuit the catalog + server calls. Build a fake
  // drill + session from the unsaved payload and auto-start it; on submit
  // grade locally and surface a result panel that closes the overlay.
  useEffect(() => {
    if (!previewMode || !previewPayload) return;
    const drill = {
      id: 'preview',
      title: previewTitle || 'Preview',
      timeLimitSeconds: previewPayload.timeLimitSeconds || 90,
    };
    const session = {
      id: 'preview-session',
      drillId: 'preview',
      drillTitle: drill.title,
      status: 'in_progress',
      snapshot: {
        scenario: previewPayload.scenario || {},
        timeLimitSeconds: previewPayload.timeLimitSeconds || 90,
      },
    };
    setActiveDrill(drill);
    setActiveSession(session);
    setResultSession(null);
    setLoading(false);
  }, [previewMode, previewPayload, previewTitle]);

  const refresh = useCallback(async () => {
    if (previewMode) return; // suppress catalog fetch in preview
    try {
      setLoading(true);
      const result = await api.listSpotErrorDrills(token);
      setDrills(result.drills || []);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load exercises.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  const startDrill = useCallback(async (drill) => {
    if (startingRef.current) return;
    startingRef.current = drill.id;
    try {
      const result = await api.startSpotErrorDrill(drill.id, token);
      setActiveSession(result.session);
      setActiveDrill(drill);
      setResultSession(null);
    } catch (err) {
      setError(err?.message || 'Could not start exercise.');
    } finally {
      startingRef.current = null;
    }
  }, [token]);

  const submitFlags = useCallback(async (flagsList, durationSeconds) => {
    if (!activeSession) return;
    if (previewMode) {
      // Grade locally — we never persisted a session to grade against.
      const grading = gradeLocallyForPreview(previewPayload?.expectedIssues || [], flagsList);
      setResultSession({
        id: 'preview-result',
        status: 'completed',
        precisionPct: grading.precisionPct,
        recallPct: grading.recallPct,
        f1Pct: grading.f1Pct,
        grading,
      });
      setActiveSession(null);
      return;
    }
    try {
      const result = await api.submitSpotErrorSession(activeSession.id, flagsList, durationSeconds, token);
      setResultSession(result.session);
      setActiveSession(null);
    } catch (err) {
      setError(err?.message || 'Could not submit.');
    }
  }, [activeSession, token, previewMode, previewPayload]);

  const abandon = () => {
    if (previewMode && onExitPreview) { onExitPreview(); return; }
    setActiveSession(null);
    setActiveDrill(null);
  };

  // Re-start the preview from scratch (fresh fake session, no result).
  const restartPreview = () => {
    if (!previewMode || !previewPayload) return;
    const session = {
      id: 'preview-session',
      drillId: 'preview',
      drillTitle: activeDrill?.title || 'Preview',
      status: 'in_progress',
      snapshot: {
        scenario: previewPayload.scenario || {},
        timeLimitSeconds: previewPayload.timeLimitSeconds || 90,
      },
    };
    setActiveSession(session);
    setResultSession(null);
  };

  if (activeSession) {
    return (
      <div className="student-page">
        <Container className="mt-4 mb-5 student-page__content spot-error-shell">
          <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
            <h2 className="mb-0">{previewMode ? 'Preview · ' : ''}{activeDrill?.title || 'Spot the Error'}</h2>
            {previewMode && onExitPreview ? (
              <Button type="button" variant="outline-secondary" onClick={onExitPreview}>
                <i className="bi bi-x-lg me-1" /> Close preview
              </Button>
            ) : null}
          </div>
          {error ? <Alert variant="danger">{error}</Alert> : null}
          <ActiveDrill
            session={activeSession}
            drill={activeDrill}
            onSubmit={submitFlags}
            onAbandon={abandon}
          />
        </Container>
      </div>
    );
  }

  if (resultSession) {
    return (
      <div className="student-page">
        <Container className="mt-4 mb-5 student-page__content spot-error-shell">
          <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
            <h2 className="mb-0">{previewMode ? 'Preview result · ' : 'Result · '}{activeDrill?.title || 'Exercise complete'}</h2>
            {previewMode && onExitPreview ? (
              <Button type="button" variant="outline-secondary" onClick={onExitPreview}>
                <i className="bi bi-x-lg me-1" /> Close preview
              </Button>
            ) : null}
          </div>
          <ResultPanel
            session={resultSession}
            drill={activeDrill}
            onPlayAgain={() => (previewMode ? restartPreview() : (activeDrill && startDrill(activeDrill)))}
            onBackToList={() => {
              if (previewMode && onExitPreview) { onExitPreview(); return; }
              setResultSession(null);
              setActiveDrill(null);
            }}
            backToListLabel={previewMode ? 'Close preview' : undefined}
          />
        </Container>
      </div>
    );
  }

  return (
    <div className="student-page">
      <Container className="mt-4 mb-5 student-page__content spot-error-shell">
        <div className="student-dashboard-header shadow-sm rounded mb-3">
          <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
            <div>
              <h2 className="mb-2">Spot the Error</h2>
              <p className="student-dashboard-header__copy mb-0">Time-pressured prescribing-error detection. Pick a chart, flag every issue, beat the clock.</p>
              {onBack ? (
                <Button type="button" variant="outline-primary" className="btn-sm mt-2" onClick={onBack}>
                  <i className="bi bi-arrow-left" aria-hidden="true" />{' '}
                  Back
                </Button>
              ) : null}
            </div>
          </div>
        </div>
        {error ? <Alert variant="danger">{error}</Alert> : null}
        {loading ? (
          <div className="text-center mt-4"><Spinner animation="border" /></div>
        ) : drills.length === 0 ? (
          <Alert variant="info" className="mt-4">
            No spot-the-error exercises available yet. Ask the platform admin to seed the spot-error library on this tenant.
          </Alert>
        ) : (
          <div className="spot-error__list">
            {drills.map((drill) => {
              const diff = labelForDifficulty(drill.difficulty);
              return (
                <Card key={drill.id} className="spot-error__list-card">
                  <Card.Body>
                    <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
                      <h5 className="mb-0">{drill.title}</h5>
                      <Badge bg={diff.tone}>{diff.label}</Badge>
                    </div>
                    <p className="text-muted small mb-2">{drill.summary}</p>
                    <div className="d-flex flex-wrap gap-2 mb-3 small text-muted">
                      <span>⏱ {drill.timeLimitSeconds}s</span>
                      <span>· {drill.prescriptionCount} prescriptions</span>
                      <span>· {drill.expectedIssueCount} planted issue{drill.expectedIssueCount === 1 ? '' : 's'}</span>
                    </div>
                    <Button variant="primary" onClick={() => startDrill(drill)} disabled={startingRef.current === drill.id}>
                      Start
                    </Button>
                  </Card.Body>
                </Card>
              );
            })}
          </div>
        )}
      </Container>
    </div>
  );
};

export default StudentSpotErrorWorkspace;
